import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Container, FlexContainer, H2 } from "@carrotfertility/carotene"
import { Steps } from "./step-library"
import useButtonSingleSelect from "../../hooks/useButtonSingleSelect"
import ButtonSelectGroup from "../../shared/ButtonSelectGroup"
import { useRoutingInfo } from "../../context/RoutingInfoContext"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"

const journeyStages = [
  { id: "EXPLORING", title: "Exploring, trying to learn more" },
  { id: "CHOOSING_AGENCY_ATTORNEY", title: "Choosing an agency and/or attorney" },
  { id: "WORKING_WITH_AGENCY_ATTORNEY", title: "Currently working with an agency and/or attorney" },
  { id: "CHOOSING_AGENCY_CLINIC_ATTORNEY", title: "Choosing a clinic, agency/consultancy, and/or attorney" },
  {
    id: "WORKING_WITH_AGENCY_CLINIC_ATTORNEY",
    title: "Currently working with a clinic, agency/consultancy, and/or attorney"
  },
  { id: "FINAL_STAGES", title: "In final stages of the process" },
  { id: "GESTATIONAL_PREGNANT", title: "The gestational carrier is pregnant" },
  { id: "OTHER", title: "Other" },
  { id: "PRESERVATION_READY", title: "Would like to receive preservation care soon" },
  { id: "PRESERVATION_STARTED", title: "Currently receiving preservation care" },
  { id: "MIGHT_WANT_IUI_IVF", title: "Might want IUI or IVF but curious about other options" },
  { id: "GETTING_READY_START_IUI", title: "Getting ready to start IUI" },
  { id: "GETTING_READY_START_IVF", title: "Getting ready to start IVF" },
  { id: "STARTED_IUI_CYCLE", title: "Already started or completed IUI cycle" },
  { id: "STARTED_IVF_CYCLE", title: "Already started or completed IVF cycle" },
  { id: "HAVE_NOT_STARTED", title: "Haven't started yet" },
  { id: "TRY_PREGNANT_STARTED", title: "Starting to try" },
  { id: "TRY_PREGNANT_STARTED_LONG", title: "Been trying for a while" },
  { id: "PREGNANT_FIRST", title: "First trimester (fewer than 14 weeks)" },
  { id: "PREGNANT_SECOND", title: "Second trimester (14-28 weeks)" },
  { id: "PREGNANT_THIRD", title: "Third trimester (more than 28 weeks)" },
  { id: "NOT_SURE_PARENT", title: "Not sure whether I want to be a parent" },
  { id: "LEARNING_FERTILITY", title: "Want to start learning about my fertility health" },
  { id: "EXPLORING_NO_PATH", title: "Interested in parenthood eventually but haven't chosen a path yet" },
  { id: "EXPLORING_MULTIPLE_PATHS", title: "Started down multiple paths to parenthood" },
  { id: "EXPLORING_NEW_OPTIONS", title: "Previous paths haven't worked out, looking for new options" }
]

const journeyTypeStageOptions = {
  ADOPTION: ["EXPLORING", "CHOOSING_AGENCY_ATTORNEY", "WORKING_WITH_AGENCY_ATTORNEY", "FINAL_STAGES"],
  GESTATIONAL: [
    "EXPLORING",
    "CHOOSING_AGENCY_CLINIC_ATTORNEY",
    "WORKING_WITH_AGENCY_CLINIC_ATTORNEY",
    "GESTATIONAL_PREGNANT",
    "OTHER"
  ],
  PRESERVATION: ["EXPLORING", "PRESERVATION_READY", "PRESERVATION_STARTED"],
  ASSISTED_REPRODUCTION: [
    "EXPLORING",
    "MIGHT_WANT_IUI_IVF",
    "GETTING_READY_START_IUI",
    "GETTING_READY_START_IVF",
    "STARTED_IUI_CYCLE",
    "STARTED_IVF_CYCLE"
  ],
  TRY_PREGNANT: ["HAVE_NOT_STARTED", "TRY_PREGNANT_STARTED", "TRY_PREGNANT_STARTED_LONG"],
  PREGNANT: ["PREGNANT_FIRST", "PREGNANT_SECOND", "PREGNANT_THIRD"],
  EXPLORING: [
    "NOT_SURE_PARENT",
    "LEARNING_FERTILITY",
    "EXPLORING_NO_PATH",
    "EXPLORING_MULTIPLE_PATHS",
    "EXPLORING_NEW_OPTIONS"
  ]
}

function useOptionsForJourneyType() {
  const { routingData } = useRoutingInfo()
  const memberJourney = routingData.journey
  const journeyStageOptions = journeyTypeStageOptions?.[memberJourney] || []
  const buttonOptions = journeyStages.filter((stage) => journeyStageOptions.includes(stage.id))
  return {
    buttonOptions
  }
}

const ViewName = Steps.WHERE_ARE_YOU_IN_JOURNEY
export default function WhereAreYouInJourneyStep(): JSX.Element {
  const { updateRoutingInfo, routingData } = useRoutingInfo()
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  const defaultValue = routingData?.journeyStage
  const { setNextStep } = useStateMachine(ViewName, defaultValue ? "" : null)
  const { buttonOptions } = useOptionsForJourneyType()
  const { valid, ...buttonProps } = useButtonSingleSelect({
    options: buttonOptions,
    defaultValue,
    onSubmit: onClickContinue
  })
  async function onClickContinue(selectedValue: string): Promise<void> {
    if (selectedValue !== null) {
      await handleContinueLoading(() => updateRoutingInfo({ journeyStage: selectedValue }))
      setNextStep(ViewName, "")
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onClickContinue(defaultValue)
      }}
    >
      <Container>
        <H2 stack="large">Where are you in this journey?</H2>
        <ButtonSelectGroup {...buttonProps} />
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="flex-end">
        <ArrowSubmitButton enabled={valid && !continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}
